import { useTranslation } from 'next-i18next'

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { ActivityProps, InputsAnswer } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFactorDivision from '~/bootcamp/Activities/Factorization/hooks/useFactorDivision'

import { generateInitialAnswers, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'

import Times from '~/bootcamp/Activities/components/Times'
import Input from '~/bootcamp/Activities/components/Input'
import Equals from '~/bootcamp/Activities/components/Equals'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import Tree from './Tree'
import { FactorBranch } from './factorTree.types'

const createEmptyBranches = (noOfBranches: number, questionCompound: number): FactorBranch[] => {
  const branches: FactorBranch[] = Array(noOfBranches)
    .fill(0)
    .map(() => ({ compound: null, showBranch: false, isCompleted: false }))

  // eslint-disable-next-line fp/no-mutating-methods
  branches.splice(0, 1, { ...branches[0], compound: questionCompound, showBranch: true })

  return branches
}

const FactorTree = ({
  difficultyLevel,
  onNextQuestionClick,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitAnswer,
  autofocus = true,
}: ActivityProps): React.ReactElement => {
  const { t } = useTranslation('generics')
  const [questionNumber, numberOfInputs, , generateNewQuestion, verifyAnswer] = useFactorDivision(difficultyLevel)
  const [branch, updateBranch] = useState<FactorBranch[]>([])
  const [answerInputs, setAnswerInputs] = useState<InputsAnswer>({})
  const [treeKey, setTreeKey] = useState(0) // To force re-render Tree on clear answer!
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const inputFieldIterators = useMemo(() => Array.from({ length: numberOfInputs }, (_, i) => i + 1), [numberOfInputs])

  useEffect(() => {
    updateBranch(createEmptyBranches(15, questionNumber))
  }, [questionNumber])

  useEmitInstance(
    onInstanceChange,
    questionNumber ? `${questionNumber} = ${Array(numberOfInputs).fill('\\square').join(' \\cdot ')}` : null,
  )

  useEffect(() => {
    const initialAnswers = generateInitialAnswers(inputFieldIterators)

    setAnswerInputs(initialAnswers)
  }, [inputFieldIterators])

  const openNode = (nodeIndices: number, nodeCompoundNumber: number, direction: 'left' | 'right'): void => {
    const newNodeIndex = direction === 'left' ? nodeIndices * 2 + 1 : nodeIndices * 2 + 2

    const newNode: FactorBranch = {
      compound: nodeCompoundNumber,
      showBranch: true,
      isCompleted: false,
    }

    if (!branch[newNodeIndex]) return

    updateBranch((exBranches) => {
      const tBranch = [...exBranches]
      // eslint-disable-next-line fp/no-mutating-methods
      tBranch.splice(nodeIndices, 1, { ...tBranch[nodeIndices], isCompleted: true })
      // eslint-disable-next-line fp/no-mutating-methods
      tBranch.splice(newNodeIndex, 1, newNode)

      return tBranch
    })
  }

  const handleAnswerInputChange = (value: string, name: string): void => {
    onAnswerStateChange(null)
    setAnswerInputs((existingInput) => ({ ...existingInput, [name]: value }))
  }

  const clearAnswer = (): void => {
    const resetAnswers = generateInitialAnswers(inputFieldIterators)
    setAnswerInputs(resetAnswers)
    updateBranch(createEmptyBranches(15, questionNumber))
    setTreeKey((key) => (key += 1))

    // Reset answer state
    onAnswerStateChange(null)
  }

  const handleOnNextQuestionClick = (): void => {
    // setTaskHistory((existHistory) => [...existHistory, questionNumber].slice(0, 3))
    generateNewQuestion()
    clearAnswer()
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const handleOnCheckAnswerClick = (): void => {
    const answer = Object.keys(answerInputs).map((key) => parseFloat(answerInputs[key]))
    const isCorrect: boolean = verifyAnswer(answer)
    onAnswerStateChange(isCorrect)
  }

  const onEnterPress = (): void => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const answerInputsList = (): React.ReactElement => {
    return (
      <Fragment>
        {inputFieldIterators.map((arrInd, index) => {
          const fieldName = `answer_${arrInd}`

          return (
            <Fragment key={fieldName}>
              <Input
                aria-label={t('ariaLabelButtons.insertPrimeFactor')}
                autoFocus={autofocus && index === 0}
                className="w-auto md:w-auto"
                isError={answerState === false}
                key={`${inputKey}-${fieldName}`}
                maxDigits={3}
                name={fieldName}
                onChange={handleAnswerInputChange}
                onEnter={onEnterPress}
                value={answerInputs[fieldName] || ''}
              />

              {arrInd !== numberOfInputs && <Times />}
            </Fragment>
          )
        })}
      </Fragment>
    )
  }

  const isFormValid = (): boolean => {
    return !Object.keys(answerInputs).some((key) => answerInputs[key] === '')
  }

  return (
    <ActivityWrapper>
      <Tree
        branch={branch}
        difficultyLevel={difficultyLevel}
        key={treeKey}
        openNode={openNode}
        primarySelectedInputRef={inputRef}
        questionNumber={questionNumber}
      />

      <div className="flex items-center justify-center">
        <p className="text-purple md:text-lg font-semibold">{questionNumber}</p>
        <Equals />
        {answerInputsList()}
      </div>

      {SubmitAnswer && (
        <SubmitAnswer
          clearFormAction={clearAnswer}
          correctAnswer={!!answerState}
          formValid={isFormValid()}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default FactorTree
